import PageSection from "controls/global/page-section/PageSection";
import PdfViewer from "controls/global/pdf-viewer/PdfViewer";
import ScrollToTopArrow from "controls/global/scroll-to-top-arrow";
import { SelectionConfig } from "controls/global/stewart-table/StewartTable";
import { FileSearchCriteria } from "entities/ApiModel";
import { IColumn } from "entities/ApiModel/IColumn";
import { PaymentsFileExportCriteria } from "entities/ApiModel/PaymentsFileExportCriteria";
import { PdfDocument } from "entities/UIModel";
import { FilePaymentSearch } from "entities/UIModel/FilePaymentSearch";
import { PendingPayCriteriaSearch } from "entities/UIModel/PendingPayCriteriaSearch";
import { PendingPayFile } from "entities/UIModel/PendingPayFile";
import { PendingPayKeywordSearch } from "entities/UIModel/PendingPayKeywordSearch";
import { LayoutBodyContext } from "layout/LayoutBodyContext";
import {
  orderBy as _orderBy,
  debounce,
} from "lodash";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import CashClockIcon from "theme/icons/CashClockIcon";
import { getColumnSearchField } from "utils/columnsGrid";
import { useConfigContext } from "utils/context/ConfigContextProvider";
import { useGlobalAccess, useHasStartedTyping, useSearchColumnToggle } from "utils/context/GlobalAccessContext";
import { useLookup } from "utils/context/LookupContext";
import { usePayment } from "utils/context/PaymentContext";
import { usePendingPay } from "utils/context/PendingPayContext";
import { useUser } from "utils/context/UserContext";
import useDidMountEffect from "utils/custom-hooks/useDidMountEffect";
import useTitle from "utils/custom-hooks/useTitle";
import {
  AgencyStatus,
  DateTypeCode,
  PaymentsFileExportCriteriaType,
  ReportingOptionType,
  SessionStorageKeys,
} from "utils/data/enum";
import { Order } from "utils/sorting";
import { v4 as uuidv4 } from "uuid";
import FileExportDialog from "./components/file-export-dialog";
import PendingPayGrid from "./components/pendingpay-grid/PendingPayGrid";
import PendingPayPageHeader from "./components/pendingpay-header/PendingPayPageHeader";
import { getPdfDocument } from "pages/file/utils/helper";
import { isSearchFilterApplied } from "utils/services/utilsService";
import { isSameDate } from "utils/shared";

const DefaultPage = 1;
const TotalRows = 100;
const NoRecordsMessage = "Loading...";
const TimeoutMessage = "Sorry, this search took too long. Please modify your search criteria and try again.";
const DefaultReportingOptions = [ReportingOptionType.ReportOnly, ReportingOptionType.AutoReport, ReportingOptionType.PayByCheck]; //, ReportingOptionType.EPay];
const DefaultDateTypeCode = DateTypeCode.ReportedDate;
const defaultSortColumn = "-LastBilledDate";
const defaultOrderBy = "lastBilledDate";
const defaultOrder = "desc";
const date = new Date();
const DefaultDateRangeStart = new Date(new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime() - (30 * 24 * 60 * 60 * 1000));

const PendingPayPage = () => {
  useTitle("Stewart Connect - Pending Payments");

  const [
    {
      error: requestError,
      progressPercent: loadingProgressPercent,
      refreshData,
      onlyShowSelectedFiles,
      isAdvSearchOpen,
      selectedItems,
    },
    {
      getPendingPayList,
      getPendingPayListLocal,
      setRequestTimeoutMs,
      cancelRequests,
      getInitialColumnsDefintion,
      getColumnsDefinition,
      setColumnDefinition,
      getAdvancedSearchFiles,
      updateSelectedItem,
      updateSelectedItems,
      setOnlyShowSelectedFiles,
      setIsAdvSearchOpen,
    }
  ] = usePendingPay();
  const defaultPendingPayCriteriaSearch: PendingPayCriteriaSearch =
  {
    listCount: TotalRows,
    requestedPage: 1,
    reportOptionTypeCode: DefaultReportingOptions
      .map(value => value.toString())
      .join("|"),
    dateTypeCode: DefaultDateTypeCode,
    dateRangeStart: DefaultDateRangeStart,
  };

  const [, { exportFiles }] = usePayment();
  const [openExportDialog, setOpenExportDialog] = useState<boolean>(false);
  const [pendingPayList, setPendingPayList] = useState<PendingPayFile[]>([]);
  const [filters, setFilters] = useState<PendingPayCriteriaSearch>(defaultPendingPayCriteriaSearch);
  const [noRecordsMessage, setNoRecordsMessage] = useState<string[]>([NoRecordsMessage]);
  const [hiddenColumns, setHiddenColumns] = useState<(keyof PendingPayFile)[]>(getInitialColumnsDefintion(true));
  const [columns, setColumns] = useState<IColumn[]>(getInitialColumnsDefintion(false));
  const [order, setOrder] = useState<Order>(defaultOrder);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filtersApplied, setFiltersApplied] = useState<boolean>(false);
  const [partialFilterUpdate, setPartialFilterUpdate] = useState<boolean>(false);
  const [settingsLoaded, setSettingsLoaded] = useState<boolean>(false);
  const [readyToSearch, setReadyToSearch] = useState<boolean>(false);
  const [userInputDebounceTimeMs, setUserInputDebounceTimeMs] = useState<number>(750);
  const bodyRef = useContext(LayoutBodyContext);
  const [orderBy, setOrderBy] = useState<keyof PendingPayFile | undefined>(defaultOrderBy);
  const [sortColumn, setSortColumn] = useState<string | undefined>(defaultSortColumn);
  const records = pendingPayList ?? [];
  const { generalConfig } = useConfigContext();
  const [openPdfViewer, setOpenPdfViewer] = useState(false);
  const [pdfDocuments, setPdfDocuments] = useState<PdfDocument[]>([]);
  const [exportedColumns, setExportedColumns] = useState<string[]>([]);
  const storedCriteriaString = sessionStorage.getItem(SessionStorageKeys.PENDING_PAY_STORED_CRITERIA);
  const storedCriteria = storedCriteriaString ? JSON.parse(storedCriteriaString) : null;
  const usePriorSearchCriteria = sessionStorage.getItem(SessionStorageKeys.USE_PRIOR_SEARCH_CRITERIA);
  const usePriorSearchCriteriaAdvanceSearch = sessionStorage.getItem(SessionStorageKeys.USE_PRIOR_SEARCH_CRITERIA_ADVANCE_SEARCH);
  const defaultValues =
    (storedCriteria && usePriorSearchCriteria) || (storedCriteria && usePriorSearchCriteriaAdvanceSearch)
      ? storedCriteria
      : null;
  const selectedStoredFiles = sessionStorage.getItem(SessionStorageKeys.SELECTED_FILES_PENDING_PAY);
  const parsedStoredFiles: PendingPayFile[] | null = selectedStoredFiles ? JSON.parse(selectedStoredFiles) : null;
  const storedSelectedItems = (parsedStoredFiles && usePriorSearchCriteria) ? parsedStoredFiles : selectedItems;
  const defaultRequestedPage = defaultValues ? storedCriteria["requestedPage"] : DefaultPage;
  const [page, setPage] = useState<number>(defaultRequestedPage);
  const companyStatus = [AgencyStatus.Active, AgencyStatus.Inactive]
  const [{ hasStartedTyping },{setHasStartedTyping}] = useHasStartedTyping();
  const [,{setIsSearchColumnOn}] = useSearchColumnToggle();

  const [,
    {
      getRowsPerPageSetting,
      setRowsPerPageSetting,
    }
  ] = useUser();
  const [, { getReportOptionTypes }] = useLookup();
  const [{ selectedAgency: globalAccessAgency }] = useGlobalAccess();
  const globalAccessAgencyID = globalAccessAgency?.[0]?.CompanyID;
  const globalAccessAgencyName = globalAccessAgency?.[0]?.CompanyName;

  const filteredPendingPayList = pendingPayList ?? [];

  const loadSettings = async () => {
    const debounceTime = generalConfig.userInputDebounceTimeMs;
    const timeoutMs = generalConfig.filesSearchTimeoutMs;
    if (debounceTime) {
      setUserInputDebounceTimeMs(debounceTime);
    }
    if (timeoutMs) {
      setRequestTimeoutMs(timeoutMs);
    }
  };

  const debounceSetReadyToSearch = useCallback(
    debounce(() => setReadyToSearch(true), userInputDebounceTimeMs)
    , [userInputDebounceTimeMs]
  );

  const getDatabaseSortColumnName = (name: string) => {
    const mappedColumnNames: { [key: string]: string; } = {
      fileSearchId: "FileSearchID",
      fileId: "ClientFileID",
      clientFileId: "ClientFileID",
      reportOptionTypeCode: "ReportOptionTypeCode",
      propertyTypeCode: "PropertyTypeCode",
      propertyAddress: "PropertyAddress",
      orderNumber: "OrderNumber",
      lastBilledBy: "LastBilledBy",
      lastBilledDate: "LastBilledDate",
      user: 'User',
      currentRow: "CurrentRow",
      currentPage: "CurrentPage",
      totalPages: "TotalPages",
      totalRows: "TotalRows",
    };
    return mappedColumnNames[name] ?? name;
  };

  const convertValue = (name: string, value: any) => {
    const fields = ["totalBilledAmountDue", "totalBilledActualFee", "totalBilledActualRiskRate", "orderNumber"];
    if (value !== null && fields.includes(name)) {
      return Number(value);
    }
    return value;
  };

  const getSearchField = (field: keyof PendingPayFile) =>
    getColumnSearchField<PendingPayFile, PendingPayCriteriaSearch>(columns, field);

  // const getDefaultFilters = (advanced: boolean): PendingPayCriteriaSearch => {
  //   const dateType = DateTypeCode.ReportedDate;
  //   const reportOptionTypeCode = advanced ? undefined : DefaultReportingOptions
  //     .map(value => value.toString())
  //     .join("|");
  //   const dateRangeStart = advanced ? undefined : DefaultDateRangeStart;

  //   const defaultFilters: PendingPayCriteriaSearch = {
  //     dateTypeCode: dateType,
  //     listCount: TotalRows,
  //     requestedPage: 1,
  //     sortColumn: sortColumn,
  //     reportOptionTypeCode,
  //     dateRangeStart,
  //   };

  //   return defaultFilters;
  // };

  // Clear limit for start date when any criteria are entered 
  const resetLimitStartDate = (object: {[key: string]: any; }) => {
    const defaultFields: string[] = [
      "criteriaType",
      "dateTypeCode",
      "dateRangeStart",
      "listCount",
      "reportOptionTypeCode",
      "requestedPage",
      "sortColumn",
    ];

    let hasAnyNonDefaultCriteria:Boolean = false;
    Object.keys(object).forEach((key) => {
      // When it's not the default parameters
      if (  object[key] && 
            !defaultFields.includes(key) 
        ) {
          hasAnyNonDefaultCriteria = true;
      }
    });

    // Check if reportOptionTypeCode is different from detault
    if (!hasAnyNonDefaultCriteria) {
      if (object["reportOptionTypeCode"] ) {
        const reportOptionTypeCodesSorted = object["reportOptionTypeCode"].split("|").sort().join('|');
        const defaultReportingOptionsSorted = DefaultReportingOptions.sort().join('|');
        if (reportOptionTypeCodesSorted !== defaultReportingOptionsSorted) {
          hasAnyNonDefaultCriteria = true;
        }
      }
    }

    // console.log("hasAnyNonDefaultCriteria:", hasAnyNonDefaultCriteria);

    if (hasAnyNonDefaultCriteria &&
      object["dateTypeCode"] === DefaultDateTypeCode &&
      (
        isSameDate(object["dateRangeStart"], DefaultDateRangeStart) &&
        !dateRangeStartUpdatedDate.current 
      )
    ) {
      // Remove default dateTypeCode and dateRangeStart
      object["dateTypeCode"] = undefined;
      object["dateRangeStart"] = undefined;
    }

    // TESTING ONLY -- in case Hilary wanted to add the default back after the date start is reset
    else if (
      !hasAnyNonDefaultCriteria &&
      object["dateTypeCode"] === DefaultDateTypeCode &&
      !dateRangeStartUpdatedDate.current 
    ) {
      // object["dateTypeCode"] = DefaultDateTypeCode;
      object["dateRangeStart"] = DefaultDateRangeStart;
    }

    return object; 
  };

  const handleRequestSort = async (columnName: string, disableToggling: boolean = false) => {
    await cancelRequests();

    let sortBy = getDatabaseSortColumnName(columnName);
    if (!disableToggling && sortColumn === sortBy) {
      sortBy = `-${sortBy}`;
    }

    setOrder(sortBy.startsWith("-") ? "desc" : "asc");
    setOrderBy(columnName.replace("-", "") as keyof PendingPayFile);
    setSortColumn(sortBy);
  };

  const handleColumnResize = (column: keyof PendingPayFile, newWidth: number) => {
    if (column && newWidth > 0) {
      setColumnDefinition([column], "width", Math.floor(newWidth));
    }
  };

  const handleToggleAllRows = async () => {
    if (pendingPayList) {
      updateSelectedItems(pendingPayList); //(pendingPayList.filter((list: PendingPayFile) => list.companyStatus === "Active"));
      reloadLocalSearch();
    }
  };

  const handleToggleRow = (file: PendingPayFile) => {
    updateSelectedItem(file);
    reloadLocalSearch();
  };

  const selectionConfig: SelectionConfig<PendingPayFile> = {
    enabled: true,
    selectedRows: storedSelectedItems?.map(s => s.fileId.toString()) ?? [],
    onToggleAllRows: handleToggleAllRows,
    onToggleRow: handleToggleRow,
    isRowSelected: (file: PendingPayFile) => storedSelectedItems?.some(f => f.fileId === file.fileId  && f.paymentSheetID === file.paymentSheetID),
  };

  const handleColumnsModified = (columns: IColumn[]) => {
    const exportedCols = columns.filter(c => !hiddenColumns.includes(c.field as keyof PendingPayFile))
      .map(c => c.field);

    setExportedColumns(exportedCols);
  };

  const handleUpdateHiddenColumns = (columns: (keyof PendingPayFile)[]) => {
    setHiddenColumns(columns);
  };

  const handleExport = async (allFiles: boolean, itemizeProducts: boolean) => {
    const requestId = uuidv4();

    let searchCriteria: FilePaymentSearch = {};
    if (allFiles) {
      // export all files
      searchCriteria = isAdvSearchOpen ? getAdvancedSearchCriteria() : getSearchCriteria();
      searchCriteria = {
        ...searchCriteria,
        criteriaType: hasStartedTyping // isAdvSearchOpen
          ? PaymentsFileExportCriteriaType.PendingPaymentsKeyword
          : PaymentsFileExportCriteriaType.PendingPaymentsCriteria,
        ...(globalAccessAgencyID && { agencyID: globalAccessAgencyID }),
      };

      // if (!isAdvSearchOpen) {
        searchCriteria = resetLimitStartDate({...searchCriteria});
      // }
    }
    else {
      // export selected only
      searchCriteria.criteriaType = PaymentsFileExportCriteriaType.PendingPaymentsCriteria;
    }

    const exportCriteria: PaymentsFileExportCriteria = {
      FileIds: allFiles
        ? []
        : storedSelectedItems.map(s => s.fileId),
      ExportColumns: [
        "clientFileId",
        ...exportedColumns.filter(c => c !== "clientFileID" && c !== "totalBilledAmountDue"),
        "totalBilledAmountDue",
      ],
      ItemizeEachProduct: itemizeProducts,
      SearchCriteria: searchCriteria,
    };

    await exportFiles(exportCriteria, requestId);
    setOpenExportDialog(false);
  };

  const visibleColumns = columns
    .filter((c) => c.hideable)
    .map((c) => ({
      field: c.field as keyof PendingPayFile,
      name: c.name,
      checked: !hiddenColumns.includes(c.field as keyof PendingPayFile),
    }));

  const reloadLocalSearch = () => {
    if (onlyShowSelectedFiles) {
      setPage(DefaultPage);
      performSearch();
    }
  };

  const handleOnlySelectedFilesChange = (value: boolean) => {
    setOnlyShowSelectedFiles(value);
  };

  const canSearch = (): boolean => {
    return settingsLoaded &&
      !partialFilterUpdate &&
      columns?.length > 0 &&
      (filters !== undefined && Object.values(filters).length > 0);
  };

  const getSearchCriteria = () => {
    let criteria: PendingPayCriteriaSearch;
    if (usePriorSearchCriteria && storedCriteria) {
      criteria = storedCriteria;
      setFilters(storedCriteria);
      sessionStorage.removeItem(SessionStorageKeys.USE_PRIOR_SEARCH_CRITERIA);
      sessionStorage.removeItem(SessionStorageKeys.USE_PRIOR_SEARCH_CRITERIA_ADVANCE_SEARCH);
    } else {
      criteria = {
        ...filters,
        listCount: rowsPerPage,
        requestedPage: page,
        ...(!filters.agencyID && globalAccessAgencyID && { agencyID: globalAccessAgencyID }),
      };
      if (sortColumn) {
        criteria = {
          ...criteria,
          sortColumn: sortColumn,
        };
      }

      for (const key in criteria) {
        const criteriaKey = key as keyof PendingPayCriteriaSearch;
        if (criteria[criteriaKey]) {
          criteria[criteriaKey] = convertValue(criteriaKey, criteria[criteriaKey]);
        }
      }

      if (readyToSearch) {
        sessionStorage.setItem(SessionStorageKeys.PENDING_PAY_STORED_CRITERIA, JSON.stringify(criteria));
      }
    }
    return criteria;
  };

  const getAdvancedSearchCriteria = () => {
    let criteria: PendingPayKeywordSearch;
    if (usePriorSearchCriteriaAdvanceSearch && storedCriteria) {
      sessionStorage.removeItem(SessionStorageKeys.USE_PRIOR_SEARCH_CRITERIA);
      criteria = storedCriteria;
      setFilters(storedCriteria);
    } else {
      criteria = {
        ...filters,
        listCount: rowsPerPage,
        requestedPage: page,
        ...(globalAccessAgencyID && { agencyID: globalAccessAgencyID }),
      };
      if (sortColumn) {
        criteria = {
          ...criteria,
          sortColumn: sortColumn,
        };
      }
    }
    if (readyToSearch) {
      sessionStorage.setItem(SessionStorageKeys.PENDING_PAY_STORED_CRITERIA, JSON.stringify(criteria));
    }
    return criteria;
  };

  const getNoRecordsMessage = () => {
    if (!filters) {
      return [NoRecordsMessage];
    }
    let firstMessage = "No Matches Found";
    const secondMessage = "Please adjust your search criteria and search again.";
    if (hasStartedTyping) {
      const keyword = (filters as PendingPayKeywordSearch).keyword;
      let searchFields: string[] = [];
      keyword && searchFields.push(keyword);
      globalAccessAgencyName && searchFields.push(globalAccessAgencyName);
      if (searchFields) {
        firstMessage = `No matches found for "${searchFields.join(", ")}"`;
      }
    }
    else {
      const keys: (keyof PendingPayCriteriaSearch)[] = [
        "clientFileId",
        "locationLegacyID",
        "orderNumber",
        "propertyAddress",
        "lastBilledBy",
        "totalBilledActualRiskRate",
        "totalBilledActualFee",
        "totalBilledAmountDue",
      ];
      const providedFields = keys
        .map(key => filters[key])
        .filter(value => !!value && value !== globalAccessAgencyID);

      if (!filters.agencyID && globalAccessAgencyName && !providedFields.includes(globalAccessAgencyName)) {
        providedFields.push(globalAccessAgencyName);
      }

      if (providedFields.length) {
        firstMessage = `No matches found for "${providedFields.join(", ")}"`;
      }
    }
    return [firstMessage, secondMessage];
  };

  const searchableFields = [
    "clientFileID",
    "orderNumber",
    "propertyAddress",
    "lastBilledBy",
    "agencyName",
    "lenderName",
    "locationDisplayName",
    "locationLegacyID",
    "propertyTypeCode",
    "stateAbbr",
    "totalBilledActualFee",
    "totalBilledActualRetention",
    "totalBilledActualRiskRate",
    "totalBilledAmountDue",
    "transactionTypeCode",
    "firstJacketEffectiveDate",
    "firstJacketIssueDate",
    "lastBilledDate"
  ];

  const performSearch = async () => {
    let files: PendingPayFile[] | undefined = [];

    if (hasStartedTyping) {
      const criteria = getAdvancedSearchCriteria();
      files = await getAdvancedSearchFiles({ ...criteria });
    } else {
      let criteria = getSearchCriteria();
      criteria = resetLimitStartDate({...criteria});
      const hasSearchFilter = isSearchFilterApplied(criteria,searchableFields,globalAccessAgencyID);
      setIsSearchColumnOn(hasSearchFilter);
      if (onlyShowSelectedFiles) {
        files = await getPendingPayListLocal({ ...criteria });
      } else {
        files = await getPendingPayList({ ...criteria });
      }
    }

  if (files !== undefined) {
    setPendingPayList(files);
    if (!files?.length) {
      setNoRecordsMessage(getNoRecordsMessage());
      setPage(1);
    }
  }
  };

  const removeInvalidProperties = (object: { [key: string]: any; }) => {
    let deleteCount = 0;
    Object.keys(object).forEach((key) => {
      const isAgencyListWithHiddenColumn = key === "agencyIDList" && hiddenColumns.includes("agencyName");

      // Remove null properties and fields that are hidden,
      // with the exception of dateRangeStart which can always be searched even if hidden.
      if (object[key] === null ||
        object[key] === undefined ||
        (!isAdvSearchOpen
          && key !== "dateRangeStart"
          && (hiddenColumns.find(c => c === key || getSearchField(c) === key) || isAgencyListWithHiddenColumn)
        )
      ) {
        delete object[key];
        deleteCount++;
      }
    });

    return { object, deleteCount };
  };

  const dateTypeCodeUpdated = useRef(false);
  const dateRangeStartUpdated = useRef(false);
  const dateRangeStartUpdatedDate = useRef<Date|undefined>(undefined);

  // console.log("dateRangeStartUpdated:", dateRangeStartUpdated);
  // console.log("dateRangeStartUpdatedDate:", dateRangeStartUpdatedDate);
  // console.log("hasStartedTyping:", hasStartedTyping);

  const handleFilterChange = async (name: keyof PendingPayCriteriaSearch, value: any) => {
    await cancelRequests();
    if (name === "dateRangeStart") {
      dateRangeStartUpdated.current = true;
      dateRangeStartUpdatedDate.current = value;
    }
    if (name === "dateTypeCode") {
      dateTypeCodeUpdated.current = true;
    }
    const formattedValue = (typeof value) === "string" ? value.trim().replaceAll(",", "|") : value;
    const updatedFilters = (filters: PendingPayCriteriaSearch | undefined) => {
      let dateType;
      if (name === "dateTypeCode") {
        dateType = value;
      }
      else if (!hasStartedTyping && name === "dateRangeStart") {
        handleRequestSort("openedTimestamp", true);
        // dateType = DateTypeCode.Created;
        if (!dateTypeCodeUpdated.current) {
          dateType = DefaultDateTypeCode;
        }
        else {
          dateType = filters?.dateTypeCode;
        }        
      }
      else {
        dateType = filters?.dateTypeCode;
      }
      let adjustedFilters: PendingPayCriteriaSearch = {
        ...filters,
        [name]: formattedValue || undefined,
        dateTypeCode: dateType,
      };

      adjustedFilters = removeInvalidProperties(adjustedFilters).object;
      return adjustedFilters;
    };

    setPage(defaultRequestedPage ? defaultRequestedPage : DefaultPage);
    setFiltersApplied(true);
    setFilters(updatedFilters);
  };

  const handleAdvancedFilterChange = (
    name: keyof PendingPayCriteriaSearch,
    value: any,
    shouldSearch: boolean
  ) => {
    if (isAdvSearchOpen) {
      setPartialFilterUpdate(!shouldSearch);
      handleFilterChange(name, value);
    }
  };

  const handleAdvanceSearchPanelOpen = async (open: boolean) => {
    await cancelRequests();
    dateTypeCodeUpdated.current = false;
    // const resetFilters = (filters: PendingPayKeywordSearch | undefined) => {
    //   setOrderBy(defaultOrderBy);
    //   setOrder(defaultOrder);
    //   return getDefaultFilters(open);
    // };

    setFiltersApplied(false);
    // setFilters(resetFilters);
    setSortColumn(undefined);
    setPartialFilterUpdate(false);
    setPage(1);
    setIsAdvSearchOpen(open);

    if(!open) {
      handleFilterChange("dateRangeStart",null)
      handleFilterChange("dateRangeEnd",null)
      handleFilterChange("userFilter",null)
      handleFilterChange("keyword",null)
      setHasStartedTyping(false)
    }
  };

  const handleRowsPerPageChange = async (count: number) => {
    await cancelRequests();

    setRowsPerPageSetting(count);
    setRowsPerPage(count);
    setPartialFilterUpdate(false);
    setPage(1);
  };

  const handleShowPaymentSheetPdf = (documentInfo: string, documentName: string) => {
    let pdfs: PdfDocument[] = [];
    if (documentInfo && documentName) {
      pdfs.push(
        getPdfDocument(
          undefined,
          undefined,
          undefined,
          undefined,
          documentInfo,
          // documentTypeCode,
          // documentPath,
          // documentName,
          `Payment Sheet ${documentName}`,
          0
        )
      );      
    }
    setPdfDocuments(pdfs);
    setOpenPdfViewer(pdfs.length > 0);
  };

  useEffect(() => {
    const getCount = async () => {
      const count = await getRowsPerPageSetting();
      setRowsPerPage(count);
    };
    getCount();
  }, [getRowsPerPageSetting]);

  useEffect(() => {
    if (!defaultValues) setPage(DefaultPage);
  }, [onlyShowSelectedFiles]);

  useEffect(() => {
    const init = async () => {
      setIsAdvSearchOpen(false);
      await loadSettings();
      await getReportOptionTypes();
      setSettingsLoaded(true);
    };

    init();
  }, []);

  useEffect(() => {
    let sortColumnChanged = false;
    if (sortColumn) {
      const sortColumnName = sortColumn.replace("-", "");
      hiddenColumns.forEach((c) => {
        const mappedName = getDatabaseSortColumnName(c);
        if (mappedName === sortColumnName) {
          setSortColumn(undefined);
          sortColumnChanged = true;
          return;
        }
      });
    }

    const updatedFilters = removeInvalidProperties({ ...filters });
    if (sortColumnChanged || updatedFilters.deleteCount || pendingPayList === undefined) {
      setFilters((filters: FileSearchCriteria | undefined) => updatedFilters.object);
    }
  }, [hiddenColumns]);

  useEffect(() => {
    const getColumns = async () => {
      const { gridColumns, gridHiddenColumns } = await getColumnsDefinition();
      setColumns(gridColumns);
      setHiddenColumns(gridHiddenColumns);
    };

    getColumns();
  }, []);

  useEffect(() => {
    if (canSearch()) {
      if (partialFilterUpdate) {
        setPartialFilterUpdate(false);
      }

      debounceSetReadyToSearch();
    }
  }, [
    columns,
    filters,
    filtersApplied,
    page,
    partialFilterUpdate,
    refreshData,
    rowsPerPage,
    settingsLoaded,
    sortColumn,
    globalAccessAgencyName,
  ]);

  useEffect(() => {
    if (requestError === "TIMEOUT") {
      setNoRecordsMessage([TimeoutMessage]);
      setPendingPayList([]);
    }
  }, [requestError]);


  useEffect(() => {
    if (readyToSearch) {
      const search = async () => {
        await performSearch();
        setReadyToSearch(false);
      };

      search();
    }
    if (!isAdvSearchOpen) setHasStartedTyping(false);
  }, [readyToSearch]);

  useEffect(() => {
    bodyRef?.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [rowsPerPage]);

  useEffect(() => {
    const exportedCols = _orderBy(columns.filter(c => !hiddenColumns.includes(c.field as keyof PendingPayFile)), "position")
      .map(c => c.field);

    setExportedColumns(exportedCols);
  }, [columns]);

  useEffect(() => {
    handleColumnsModified(columns);
  }, [hiddenColumns]);

  useDidMountEffect(() => {
    performSearch();
  }, [onlyShowSelectedFiles]);

  useEffect(() => {
    if (!usePriorSearchCriteria) {
      try {
        sessionStorage.setItem(SessionStorageKeys.SELECTED_FILES_PENDING_PAY, JSON.stringify(storedSelectedItems));
      } catch (e) {
        sessionStorage.removeItem(SessionStorageKeys.SELECTED_FILES_PENDING_PAY);
        try {
          sessionStorage.setItem(SessionStorageKeys.SELECTED_FILES_PENDING_PAY, JSON.stringify(storedSelectedItems));
        } catch {
          // Storage quota still exceeded after clearing — skip persisting selection
        }
      }
    }
  }, [storedSelectedItems])

  return (
    <>
      <PageSection
        icon={<CashClockIcon />}
        title="Pending Payments"
        isError={false}
        contentAlignment="beside-header"
      >
        <>
          <PendingPayPageHeader
            columns={visibleColumns}
            hiddenColumns={hiddenColumns}
            updateHiddenColumns={handleUpdateHiddenColumns}
            handleAdvancedFilterChange={handleAdvancedFilterChange}
            handleAdvanceSearchPanelOpen={handleAdvanceSearchPanelOpen}
            setOpenExportDialog={setOpenExportDialog}
          />
          <PendingPayGrid
            colsConfig={columns}
            rows={records}
            loadingProgressPercent={loadingProgressPercent}
            noRowsMessage={noRecordsMessage}
            page={page}
            hiddenColumns={hiddenColumns}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            currentFilter={filters}
            reportingOptions={DefaultReportingOptions}
            selectionConfig={selectionConfig}
            isAdvSearchOpen={isAdvSearchOpen}
            onFiltersChange={handleFilterChange}
            onOnlySelectedFilesChange={handleOnlySelectedFilesChange}
            onlyShowSelectedFiles={onlyShowSelectedFiles}
            onRowsPerPageChange={handleRowsPerPageChange}
            onShowPdf={handleShowPaymentSheetPdf}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            onColumnResize={handleColumnResize}
            onColumnsModified={handleColumnsModified}
            defaultValues={storedCriteria}
            companyStatus={companyStatus}
          />
          <FileExportDialog
            open={openExportDialog}
            onCancel={() => setOpenExportDialog(false)}
            onExport={handleExport}
            showSelectionPrompt={storedSelectedItems?.some(s => s)}
            selectionPrompt="Would you like to include the selected file(s) only or all reported files?"
            showItemizedPrompt={true}
            itemizedPrompt="Would you like this export to display itemized products?"
          />
        </>
      </PageSection >
      <PdfViewer
        isOpen={openPdfViewer}
        onClose={() => setOpenPdfViewer(false)}
        pdfDocuments={pdfDocuments}
      />
      <ScrollToTopArrow refreshSize={filteredPendingPayList} />
    </>
  );
};

export default PendingPayPage;
